// Capture requests ("Show me the issue"): a workflow, AI agent, Kai or a
// teammate asks the customer for a screenshot or a screen recording. The card
// lives on a comment (`data.captureRequest`), the host SDK does the capturing,
// the Messenger renders the card, the annotation editor and the upload
// fallback. Wire shapes follow the cross-repo capture contract (v1).
//
// Everything in here is pure (no store / DOM access) so the card logic stays
// easy to reason about; the stateful side lives in Stores/Capture.ts.

export type CaptureKind = "screenshot" | "recording" | "any";
export type CaptureMediaKind = "screenshot" | "recording";

export type CaptureStatus =
  | "pending"
  | "in_progress"
  | "completed"
  | "skipped"
  | "cancelled"
  | "expired";

export const FINAL_CAPTURE_STATUSES: CaptureStatus[] = [
  "completed",
  "skipped",
  "cancelled",
  "expired",
];

export const isFinalCaptureStatus = (status?: string | null): boolean =>
  !!status && (FINAL_CAPTURE_STATUSES as string[]).includes(status);

/**
 * The customer-visible "Diagnostic data was shared with the support team."
 * line the server posts (as a BOT comment) when background logs arrive. It is
 * a system line, not a bot answer: it never ends the bot's turn, never counts
 * as a reply, and never stands in for the awaiting workflow step — it can land
 * after a parked step, whenever the SDK delivers the logs.
 */
export const isCaptureLogsNotice = (comment?: { data?: any } | null): boolean =>
  comment?.data?.captureLogsNotice === true;

/** `comment.data.captureRequest` (contract §3 CardData). */
export interface CaptureCardData {
  id: string;
  kind: CaptureKind;
  status?: CaptureStatus;
  allowUpload?: boolean;
  annotate?: boolean;
  maxDurationSec?: number;
  audio?: boolean;
  /**
   * Kai intake (kai-intake CONTRACT §3.2): show the screenshot the host sent
   * when the widget opened (`screenshot-update`, native SDKs) first.
   */
  offerHostScreenshot?: boolean;
}

/** Host → Messenger `capture-capabilities` (contract §7). */
export interface CaptureHostCapabilities {
  version?: number;
  platform?: "web" | "ios" | "android";
  sdkType?: string;
  sdkVersion?: string;
  screenshot?: boolean;
  recording?: boolean;
  recordingMethod?: "display" | "rrweb" | "frames";
  annotate?: boolean;
  maxRecordingSec?: number;
  microphone?: boolean;
  /** Pause / resume while recording (capture-state `paused`). */
  pause?: boolean;
  /**
   * The host draws the result flying into the card (`capture-fly` →
   * `capture-fly-done`). Hosts without it get the card's plain fade.
   */
  flyIn?: boolean;
}

/** The recording limit when a request doesn't set one (seconds). */
export const DEFAULT_CAPTURE_MAX_RECORDING_SEC = 120;

/** A screen rectangle (client coordinates of the frame that measured it). */
export interface CaptureRect {
  x: number;
  y: number;
  width: number;
  height: number;
}

export type CaptureHostStateName =
  | "bar"
  | "capturing"
  | "recording"
  | "preview"
  | "uploading"
  | "done"
  | "cancelled"
  | "failed"
  | "declined"
  | "unsupported";

export const CAPTURE_HOST_STATES: CaptureHostStateName[] = [
  "bar",
  "capturing",
  "recording",
  "preview",
  "uploading",
  "done",
  "cancelled",
  "failed",
  "declined",
  "unsupported",
];

/** Where this Messenger instance is with a request (never sent anywhere). */
export type CaptureLocalPhase =
  | "idle"
  | "claiming"
  | "host"
  | "editing"
  | "uploading"
  | "completing"
  | "skipping"
  | "done"
  | "skipped";

export type CaptureIssue =
  | "permissionDenied"
  | "notSupported"
  | "failed"
  | "uploadTooLarge";

export type CaptureBusyAction = "screenshot" | "recording" | "upload" | "skip";

export interface CaptureRequestLocalState {
  phase: CaptureLocalPhase;
  busyAction?: CaptureBusyAction;
  hostState?: CaptureHostStateName;
  /** 0..1 — host upload progress or the Messenger's own upload. */
  progress?: number;
  elapsedSec?: number;
  /** Date.now() when elapsedSec arrived; lets the card tick between updates. */
  elapsedAt?: number;
  issue?: CaptureIssue;
  /** Kinds the host reported `unsupported` for — their button stays hidden. */
  unsupportedKinds?: CaptureMediaKind[];
  claimedElsewhere?: boolean;
  /** What the host was asked to capture last (capture-start kind). */
  activeKind?: CaptureMediaKind;
  /** Final status learned from a 410 / GET before the comment update lands. */
  finalStatus?: CaptureStatus;
  thumbnailUrl?: string;
  sentKind?: CaptureMediaKind;
  /** The host's recording is paused (capture-state `paused`). */
  paused?: boolean;
  /** Length of the sent recording (host `done.durationSec`). */
  durationSec?: number;
  /** The sent recording has sound (host `done.audio`). */
  audio?: boolean;
  /** The sent screenshot carries markup. */
  annotated?: boolean;
  /** A file picked with "Upload a file" (name / bytes for its row). */
  fileName?: string;
  fileSize?: number;
  sentVia?: "capture" | "upload";
  /** Date.now() when this device sent it ("Just now"). */
  sentAt?: number;
  /** The result is flying into the card; its media slot waits meanwhile. */
  flyPending?: boolean;
  /** Where the flight starts (the editor's image, in frame coordinates). */
  flyFrom?: CaptureRect;
  /** What flies: a small data: URL image (hosts can't load our blob: URLs). */
  flySrc?: string;
}

/**
 * One annotation as sent to /complete (contract §2): coordinates normalized
 * 0..1 against the SENT image (after cropping). Rect / pen / text / blur use
 * their bounding box; an arrow carries its tail in x/y and the vector to its
 * head in w/h (so the head sits at x+w, y+h — w/h may be negative).
 */
export interface CaptureAnnotation {
  type: "rect" | "arrow" | "pen" | "text" | "blur";
  x: number;
  y: number;
  w: number;
  h: number;
  text?: string;
  color?: string;
}

export const DEFAULT_CAPTURE_INCLUDE = {
  consoleLog: true,
  networkLogs: true,
  customData: true,
  metaData: true,
  customEventLog: true,
  replays: false,
};

const CAPTURE_KINDS: CaptureKind[] = ["screenshot", "recording", "any"];

/**
 * The card data off a comment, or undefined when it's missing / malformed.
 * Server data is trusted for shape only — anything unexpected falls back to a
 * safe default instead of throwing inside the message list.
 */
export const parseCaptureCardData = (
  raw: any,
  fallback?: Partial<CaptureCardData>,
): CaptureCardData | undefined => {
  const source = raw && typeof raw === "object" ? raw : {};
  const id = source.id ?? fallback?.id;
  if (!id || typeof id !== "string") {
    return undefined;
  }
  const kind = CAPTURE_KINDS.includes(source.kind)
    ? source.kind
    : CAPTURE_KINDS.includes(fallback?.kind as CaptureKind)
      ? (fallback?.kind as CaptureKind)
      : "any";
  const status =
    typeof source.status === "string" ? source.status : fallback?.status;
  const pick = <T>(key: keyof CaptureCardData, type: string): T | undefined =>
    typeof source[key] === type
      ? source[key]
      : typeof (fallback as any)?.[key] === type
        ? (fallback as any)[key]
        : undefined;
  return {
    id,
    kind,
    status: status as CaptureStatus | undefined,
    allowUpload: pick<boolean>("allowUpload", "boolean"),
    annotate: pick<boolean>("annotate", "boolean"),
    maxDurationSec: pick<number>("maxDurationSec", "number"),
    audio: pick<boolean>("audio", "boolean"),
    offerHostScreenshot: pick<boolean>("offerHostScreenshot", "boolean"),
  };
};

/**
 * `comment.updated` for a capture card (contract §3: the server bumps
 * `data.captureRequest.status` and re-emits the comment). Merges into the
 * rendered comment instead of replacing it, so fields the realtime payload
 * leaves out survive.
 */
export const mergeCaptureCommentUpdate = <T extends { data?: any }>(
  comment: T,
  update: any,
): T => {
  const updatedData =
    update?.data && typeof update.data === "object" ? update.data : {};
  return {
    ...comment,
    data: {
      ...(comment.data ?? {}),
      ...updatedData,
      captureRequest: {
        ...(comment.data?.captureRequest ?? {}),
        ...(updatedData.captureRequest ?? {}),
      },
    },
  };
};

export interface CaptureSupport {
  /** False while a capable host may still announce itself. */
  resolved: boolean;
  screenshot: boolean;
  recording: boolean;
}

/**
 * What this frame can capture. Only the widget frame ever gets
 * `capture-capabilities`; the chatbar (and any host that never sent them —
 * legacy SDKs, `setCaptureEnabled(false)`) falls back to upload-only.
 */
export const resolveCaptureSupport = (args: {
  capabilities?: CaptureHostCapabilities;
  legacyHost?: boolean;
  isChatbar?: boolean;
}): CaptureSupport => {
  if (args.isChatbar) {
    return { resolved: true, screenshot: false, recording: false };
  }
  if (args.capabilities) {
    return {
      resolved: true,
      screenshot: args.capabilities.screenshot === true,
      recording: args.capabilities.recording === true,
    };
  }
  if (args.legacyHost) {
    return { resolved: true, screenshot: false, recording: false };
  }
  return { resolved: false, screenshot: false, recording: false };
};

export type CaptureProgressLabel =
  | "waiting"
  | "capturing"
  | "recording"
  | "preview"
  | "uploading"
  | "editing";

export type CaptureCardView =
  | {
      type: "pending";
      screenshot: boolean;
      recording: boolean;
      upload: boolean;
      /** Upload is the only way to answer — render it as the main action. */
      uploadIsPrimary: boolean;
      skip: boolean;
      /** Capture buttons are shown but can't be used right now. */
      captureDisabled: boolean;
      busy?: CaptureBusyAction;
      issue?: CaptureIssue;
    }
  | {
      type: "progress";
      label: CaptureProgressLabel;
      /** What is being captured (the bar's flavour). */
      kind?: CaptureMediaKind;
      progress?: number;
      elapsedSec?: number;
      elapsedAt?: number;
      paused?: boolean;
      /** The Messenger's own upload of a picked file. */
      fileName?: string;
      canCancel: boolean;
      claimedElsewhere: boolean;
    }
  | ({ type: "sent" } & CaptureSentDetails)
  | { type: "skipped" }
  | { type: "expired" }
  | { type: "cancelled" };

/** What the "Sent" card shows; all optional (a reload knows none of it). */
export interface CaptureSentDetails {
  thumbnailUrl?: string;
  sentKind?: CaptureMediaKind;
  durationSec?: number;
  audio?: boolean;
  annotated?: boolean;
  fileName?: string;
  fileSize?: number;
  sentVia?: "capture" | "upload";
  sentAt?: number;
  flyPending?: boolean;
}

const sentDetails = (
  local?: CaptureRequestLocalState,
): CaptureSentDetails => ({
  thumbnailUrl: local?.thumbnailUrl,
  sentKind: local?.sentKind,
  durationSec: local?.durationSec,
  audio: local?.audio,
  annotated: local?.annotated,
  fileName: local?.fileName,
  fileSize: local?.fileSize,
  sentVia: local?.sentVia,
  sentAt: local?.sentAt,
  flyPending: local?.flyPending,
});

const HOST_PROGRESS_LABELS: Partial<
  Record<CaptureHostStateName, CaptureProgressLabel>
> = {
  bar: "waiting",
  capturing: "capturing",
  recording: "recording",
  preview: "preview",
  uploading: "uploading",
};

/**
 * The card's view model. Server truth wins for final states (the comment's
 * status, or one learned from a 410); local host / upload state only drives
 * the card while the request is still open on the server.
 */
export const resolveCaptureCardView = (args: {
  card: CaptureCardData;
  local?: CaptureRequestLocalState;
  support: CaptureSupport;
  /** Another request's capture is running in this frame. */
  otherCaptureActive?: boolean;
}): CaptureCardView => {
  const { card, local, support } = args;
  const serverStatus = isFinalCaptureStatus(card.status)
    ? card.status
    : local?.finalStatus;

  if (serverStatus === "completed") {
    return { type: "sent", ...sentDetails(local) };
  }
  if (serverStatus === "skipped") {
    return { type: "skipped" };
  }
  if (serverStatus === "expired") {
    return { type: "expired" };
  }
  if (serverStatus === "cancelled") {
    return { type: "cancelled" };
  }

  const phase = local?.phase ?? "idle";
  if (phase === "done") {
    return { type: "sent", ...sentDetails(local) };
  }
  if (phase === "skipped") {
    return { type: "skipped" };
  }
  if (phase === "host" || phase === "editing") {
    const label: CaptureProgressLabel =
      phase === "editing"
        ? "editing"
        : (local?.hostState && HOST_PROGRESS_LABELS[local.hostState]) ||
          "waiting";
    return {
      type: "progress",
      label,
      kind: local?.activeKind,
      progress: label === "uploading" ? local?.progress : undefined,
      elapsedSec: label === "recording" ? local?.elapsedSec : undefined,
      elapsedAt: label === "recording" ? local?.elapsedAt : undefined,
      paused: label === "recording" && local?.paused === true,
      // The host owns an upload once it started it — cancelling mid-upload
      // is the host's call (its preview has the Cancel button).
      canCancel: label !== "uploading",
      claimedElsewhere: !!local?.claimedElsewhere,
    };
  }
  if (phase === "uploading" || phase === "completing") {
    return {
      type: "progress",
      label: "uploading",
      kind: local?.activeKind,
      progress: phase === "completing" ? 1 : local?.progress ?? 0,
      fileName: local?.fileName,
      canCancel: false,
      claimedElsewhere: !!local?.claimedElsewhere,
    };
  }

  const unsupported = local?.unsupportedKinds ?? [];
  const wantsScreenshot = card.kind === "screenshot" || card.kind === "any";
  const wantsRecording = card.kind === "recording" || card.kind === "any";
  const screenshot =
    wantsScreenshot && support.screenshot && !unsupported.includes("screenshot");
  const recording =
    wantsRecording && support.recording && !unsupported.includes("recording");
  const hasCapture = screenshot || recording;
  const issue = local?.issue;
  // Upload is the universal fallback: offered when the requester allows it,
  // and always when this device can't capture (legacy host, chatbar, a denied
  // or failed capture) — otherwise the card would be a dead end. The composer's
  // paperclip completes the request server-side anyway.
  const upload =
    card.allowUpload !== false ||
    (support.resolved && !hasCapture) ||
    issue === "permissionDenied" ||
    issue === "notSupported" ||
    issue === "failed";

  return {
    type: "pending",
    screenshot,
    recording,
    upload,
    uploadIsPrimary: upload && !hasCapture,
    skip: true,
    captureDisabled: !!args.otherCaptureActive,
    busy:
      phase === "claiming" || phase === "skipping"
        ? local?.busyAction
        : undefined,
    issue,
  };
};

/** `m:ss`, e.g. 0:07 / 1:42. */
export const formatCaptureElapsed = (seconds?: number): string => {
  const total = Math.max(0, Math.floor(seconds ?? 0));
  const minutes = Math.floor(total / 60);
  const rest = total % 60;
  return `${minutes}:${rest < 10 ? "0" : ""}${rest}`;
};

/**
 * The recording limit as a short phrase: "2 min", "1:30 min", "45 s".
 */
export const formatCaptureLimit = (seconds?: number): string => {
  const total = Math.max(
    0,
    Math.round(seconds ?? DEFAULT_CAPTURE_MAX_RECORDING_SEC),
  );
  if (total < 60) {
    return `${total} s`;
  }
  if (total % 60 === 0) {
    return `${total / 60} min`;
  }
  return `${formatCaptureElapsed(total)} min`;
};

/** "184 KB", "2.4 MB". */
export const formatCaptureFileSize = (bytes?: number): string | undefined => {
  if (typeof bytes !== "number" || !isFinite(bytes) || bytes <= 0) {
    return undefined;
  }
  if (bytes < 1024 * 1024) {
    return `${Math.max(1, Math.round(bytes / 1024))} KB`;
  }
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

/** Platform reported to the capture endpoints when the host didn't say. */
export const detectCapturePlatform = (args: {
  capabilities?: CaptureHostCapabilities;
  isApp?: boolean;
  userAgent?: string;
}): "web" | "ios" | "android" => {
  const platform = args.capabilities?.platform;
  if (platform === "web" || platform === "ios" || platform === "android") {
    return platform;
  }
  if (!args.isApp) {
    return "web";
  }
  return /Android/i.test(args.userAgent ?? "") ? "android" : "ios";
};

/** `accept` for the upload fallback's file input. */
export const captureAcceptForKind = (kind: CaptureKind): string => {
  if (kind === "screenshot") {
    return "image/*";
  }
  if (kind === "recording") {
    return "video/*";
  }
  return "image/*,video/*";
};

const EXTENSION_MIME_TYPES: Record<string, string> = {
  jpg: "image/jpeg",
  jpeg: "image/jpeg",
  png: "image/png",
  gif: "image/gif",
  webp: "image/webp",
  heic: "image/heic",
  heif: "image/heif",
  mp4: "video/mp4",
  m4v: "video/mp4",
  mov: "video/quicktime",
  webm: "video/webm",
  "3gp": "video/3gpp",
  mkv: "video/x-matroska",
};

/**
 * A picked file's MIME type. Some Android pickers hand over files without a
 * type, so the extension fills in; anything that is neither an image nor a
 * video is refused (the server only accepts those for captures).
 */
export const captureFileMimeType = (file: {
  type?: string;
  name?: string;
}): string | undefined => {
  const declared = (file.type ?? "").toLowerCase();
  if (declared.startsWith("image/") || declared.startsWith("video/")) {
    return declared;
  }
  const extension = (file.name ?? "").split(".").pop()?.toLowerCase() ?? "";
  return EXTENSION_MIME_TYPES[extension];
};

/** Only data-URL images from the host are ever loaded into the editor. */
export const isCaptureImageDataUrl = (value: unknown): value is string =>
  typeof value === "string" &&
  /^data:image\/(jpeg|jpg|png|webp);base64,/i.test(value);
